import {
  Flex,
  Text,
  Box,
  Image,
  Button,
  VStack,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import logo from "../../assets/logo.png";
import FormikInput from "components/formikInput";
import kidsImage from "../../assets/register-image-kids.png";
import useCreateNewPassword from "api/mutations/useCreateNewPassword";

const CreateNewPassword = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Support both: location.state (OTP flow) and URL query params (email link flow)
  const token = location.state?.token || searchParams.get("token") || "";

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("New password cannot be empty"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Please confirm your new password"),
    }),
    onSubmit: (values) => {
      handleCreateNewPassword(values);
      // Handle password update logic here
    },
  });

  const createNewPasswordMutation = useCreateNewPassword({
    onSuccess: (res) => {
      if (res.status === 200) {
        toast({
          title: "Password updated successfully!",
          description: "You can now log in using your new password.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate("/login");
      }
    },
    onError: (err) => {
      const response = err.response;
      toast({
        title: "Password update failed.",
        description:
          response?.data?.message ||
          "Something went wrong. Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleCreateNewPassword = async (values) => {
    try {
      const data = {
        token: token,
        password: values?.confirmPassword,
      };
      createNewPasswordMutation.mutate(data);
    } catch (e) {
      toast({
        title: "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      justify="center"
      align="center"
      height="100vh"
      bg="white"
      border="1px solid #2a4567"
      position="relative"
      boxShadow="0px 4px 20px rgba(0, 0, 0, 0.25)"
    >
      {/* Left Content */}
      <Box
        flex={1}
        justifyContent="center"
        alignItems="center"
        display={"flex"}
      >
        <VStack align="flex-center" spacing={8} width={400}>
          <Image src={logo} width={"425px"} height={"100px"} />
          <Text
            fontFamily={"PlusJakartaSansMedium"}
            fontSize={"36px"}
            lineHeight={"38px"}
            color={"#000000"}
            textAlign={"center"}
          >
            Create New Password
          </Text>

          <VStack align="flex-center" spacing={7}>
            <FormikInput
              name="newPassword"
              label="New Password"
              placeholder={"Enter Your Password"}
              isPassword={true}
              formik={formik}
            />
            <FormikInput
              name="confirmPassword"
              label="Confirm New Password"
              placeholder={"Re-enter Your Password"}
              isPassword={true}
              formik={formik}
            />
            <Button
              bgColor={"#456EFF"}
              colorScheme="#456EFF"
              borderRadius={"12px"}
              fontFamily={"PlusJakartaSansSemiBold"}
              fontSize={"16px"}
              lineHeight={"20px"}
              color={"#FFFFFF"}
              textAlign={"center"}
              w={"100%"}
              h={"48px"}
              _hover={{
                bg: "#456EFF",
                color: "#FFFFFF",
              }}
              mt={1}
              onClick={formik.handleSubmit}
              isDisabled={
                !formik.values.newPassword || !formik.values.confirmPassword
              }
            >
              {createNewPasswordMutation.isLoading ? <Spinner /> : "Continue"}
            </Button>
          </VStack>
        </VStack>
      </Box>
      {/* Right Image */}
      <Box flex={1}>
        <Image
          src={kidsImage}
          alt="Kids Fashion"
          objectFit="cover"
          width={589}
          height={440}
        />
      </Box>
    </Flex>
  );
};

export default CreateNewPassword;
